/**
 * Quick View Drawer
 * - Opens a sidebar drawer with product details fetched via Section Rendering API
 * - Closes on: close button, overlay click, outside click, Escape key
 * - Intercepts the quick-view add-to-cart form and opens the theme's cart drawer
 *   instead of doing a full page redirect to /cart
 */

class QuickViewDrawer extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('#QuickViewContent');
    this.overlay = this.querySelector('.quick-view-drawer__overlay');
    this.panel = this.querySelector('.quick-view-drawer__panel');
    this.closeButtons = this.querySelectorAll('[data-quick-view-close]');

    this.closeButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    // Handles both overlay clicks and any click outside the panel while open
    document.addEventListener('click', (event) => {
      if (!this.hasAttribute('open')) return;
      const clickedInsidePanel = this.panel && this.panel.contains(event.target);
      const clickedTrigger = event.target.closest('.quick-view-toggle');
      if (!clickedInsidePanel && !clickedTrigger) {
        this.close();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.hasAttribute('open')) this.close();
    });

    this._cache = new Map();
  }

  async openWithUrl(url) {
    this.setAttribute('open', '');
    this.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';

    if (this._cache.has(url)) {
      this.content.innerHTML = this._cache.get(url);
      return;
    }

    this.content.innerHTML = '<div class="loading-spinner"></div>';

    if (this._controller) this._controller.abort();
    this._controller = new AbortController();

    try {
      const separator = url.includes('?') ? '&' : '?';
      const response = await fetch(`${url}${separator}section_id=quick-view`, {
        signal: this._controller.signal,
      });
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const section = doc.querySelector('[data-quick-view-section]');
      const html = section ? section.innerHTML : 'Sorry, this product could not be loaded.';
      this._cache.set(url, html);
      this.content.innerHTML = html;
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.content.innerHTML = 'Sorry, something went wrong loading this product.';
      }
    }
  }

  close() {
    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }
}

if (!customElements.get('quick-view-drawer')) {
  customElements.define('quick-view-drawer', QuickViewDrawer);
}

/* -------------------------------------------------------------------- */
/* Open drawer when a .quick-view-toggle button is clicked              */
/* -------------------------------------------------------------------- */

document.addEventListener(
  'click',
  (event) => {
    const trigger = event.target.closest('.quick-view-toggle');
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    // Extra safety: if the button ended up nested inside an <a>, block that
    // anchor's default navigation too.
    const parentAnchor = trigger.closest('a');
    if (parentAnchor) {
      parentAnchor.addEventListener('click', (e) => e.preventDefault(), {
        once: true,
        capture: true,
      });
    }

    const drawer = document.getElementById('QuickViewDrawer');
    if (drawer) drawer.openWithUrl(trigger.dataset.quickViewUrl);
  },
  true
);

/* -------------------------------------------------------------------- */
/* Size/option pill selection — variant matching                        */
/* -------------------------------------------------------------------- */

document.addEventListener('change', (event) => {
  const input = event.target.closest('.quick-view__pill-input');
  if (!input) return;

  const form = input.closest('#quick-view-form');
  if (!form) return;

  updateSelectedVariant(form);
});

function updateSelectedVariant(form) {
  const container = form.closest('[data-quick-view-section]') || document;
  const variantsScript = container.querySelector('[data-quick-view-variants]');
  if (!variantsScript) return;

  let variants;
  try {
    variants = JSON.parse(variantsScript.textContent);
  } catch (e) {
    return;
  }

  // Gather selected values in option order (option-0, option-1, ...)
  const optionGroups = Array.from(form.querySelectorAll('.quick-view__option')).sort(
    (a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex)
  );

  const selectedValues = optionGroups.map((group) => {
    const checked = group.querySelector('.quick-view__pill-input:checked');
    return checked ? checked.value : null;
  });

  if (selectedValues.some((v) => v === null)) return;

  const matchedVariant = variants.find((variant) =>
    variant.options.every((optionValue, index) => optionValue === selectedValues[index])
  );

  const variantIdInput = form.querySelector('[data-quick-view-variant-id]');
  const submitBtn = form.querySelector('[data-quick-view-submit]');
  const submitText = form.querySelector('[data-quick-view-submit-text]');
  const unavailableMsg = container.querySelector('[data-quick-view-unavailable]');
  const priceEl = container.querySelector('[data-quick-view-price]');
  const comparePriceEl = container.querySelector('[data-quick-view-compare-price]');

  if (!matchedVariant) {
    // No variant exists for this combination at all
    if (variantIdInput) variantIdInput.value = '';
    if (submitBtn) submitBtn.setAttribute('disabled', 'disabled');
    if (submitText) submitText.textContent = 'Unavailable';
    if (unavailableMsg) unavailableMsg.removeAttribute('hidden');
    return;
  }

  if (variantIdInput) variantIdInput.value = matchedVariant.id;

  if (priceEl) priceEl.textContent = matchedVariant.price;
  if (comparePriceEl) {
    if (matchedVariant.compare_at_price) {
      comparePriceEl.textContent = matchedVariant.compare_at_price;
      comparePriceEl.removeAttribute('hidden');
    } else {
      comparePriceEl.textContent = '';
      comparePriceEl.setAttribute('hidden', '');
    }
  }

  if (matchedVariant.available) {
    if (submitBtn) submitBtn.removeAttribute('disabled');
    if (submitText) submitText.textContent = 'Add to cart';
    if (unavailableMsg) unavailableMsg.setAttribute('hidden', '');
  } else {
    if (submitBtn) submitBtn.setAttribute('disabled', 'disabled');
    if (submitText) submitText.textContent = 'Sold out';
    if (unavailableMsg) unavailableMsg.removeAttribute('hidden');
  }
}

/* -------------------------------------------------------------------- */
/* Intercept the quick-view add-to-cart form                            */
/* -------------------------------------------------------------------- */

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('#quick-view-form');
  if (!form) return;

  event.preventDefault();

  const submitBtn = form.querySelector('[type="submit"]');
  const originalBtnText = submitBtn ? submitBtn.innerHTML : null;
  if (submitBtn) {
    submitBtn.setAttribute('disabled', 'disabled');
    submitBtn.setAttribute('aria-busy', 'true');
  }
  clearFormError(form);

  const routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  try {
    const formData = new FormData(form);

    const response = await fetch(`${routeRoot}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });

    const result = await response.json();

    if (!response.ok || result.status) {
      showFormError(form, result.description || result.message || 'Could not add this item to your cart.');
      return;
    }

    const quickView = document.getElementById('QuickViewDrawer');
    if (quickView) quickView.close();

    await updateCartDrawer(result, routeRoot);
  } catch (error) {
    console.error('Quick view add to cart failed:', error);
    showFormError(form, 'Something went wrong. Please try again.');
  } finally {
    if (submitBtn) {
      submitBtn.removeAttribute('disabled');
      submitBtn.removeAttribute('aria-busy');
      if (originalBtnText !== null) submitBtn.innerHTML = originalBtnText;
    }
  }
});

async function updateCartIconBubble(routeRoot) {
  try {
    const response = await fetch(`${routeRoot}cart.js`);
    const cart = await response.json();
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble, .cart-count-bubble');
    if (bubble) bubble.textContent = cart.item_count;
  } catch (e) {
    /* ignore */
  }
}

function showFormError(form, message) {
  let errorEl = form.querySelector('.quick-view__form-error');
  if (!errorEl) {
    errorEl = document.createElement('p');
    errorEl.className = 'quick-view__form-error';
    form.appendChild(errorEl);
  }
  errorEl.textContent = message;
}

function clearFormError(form) {
  const errorEl = form.querySelector('.quick-view__form-error');
  if (errorEl) errorEl.remove();
}

async function updateCartDrawer(addResult, routeRoot) {
  const existingDrawer = document.querySelector('cart-drawer');

  if (existingDrawer) {
    try {
      // Refetch the cart-drawer section fresh and swap the ENTIRE element
      // (outerHTML, not just its inner content). This mirrors exactly what
      // happens on a normal page reload — which is known to render the
      // cart contents correctly — and avoids partial/broken state from
      // manually splicing innerHTML into a live custom element.
      const response = await fetch(`${routeRoot}?section_id=cart-drawer`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const newDrawer = doc.querySelector('cart-drawer');

      if (newDrawer) {
        existingDrawer.outerHTML = newDrawer.outerHTML;
      }
    } catch (error) {
      console.error('Failed to refresh cart drawer contents:', error);
    }

    // Re-query, since outerHTML replacement creates a brand new element node.
    const cartDrawer = document.querySelector('cart-drawer');
    updateCartIconBubble(routeRoot);

    try {
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else if (cartDrawer) {
        cartDrawer.classList.add('animate', 'active');
        cartDrawer.removeAttribute('hidden');
      }
      return;
    } catch (error) {
      console.error('cart-drawer.open() failed:', error);
    }
  }

  const cartNotification = document.querySelector('cart-notification');
  if (cartNotification && typeof cartNotification.renderContents === 'function') {
    cartNotification.renderContents(addResult);
    if (typeof cartNotification.open === 'function') cartNotification.open();
    return;
  }

  // Last-resort fallback: no known drawer component found on the page.
  // Refresh the cart icon bubble count if it exists, and notify listeners.
  await updateCartIconBubble(routeRoot);
  document.dispatchEvent(new CustomEvent('cart:refresh', { detail: addResult }));
}

/* -------------------------------------------------------------------- */
/* Media slider — arrows, thumbnails, swipe                             */
/* -------------------------------------------------------------------- */

function getSliderEls(slider) {
  const track = slider.querySelector('[data-quick-view-slider-track]');
  const slides = track ? Array.from(track.children) : [];
  const container = slider.closest('[data-quick-view-section]') || document;
  const thumbs = container.querySelectorAll('[data-quick-view-thumb]');
  return { track, slides, thumbs };
}

function goToSlide(slider, index) {
  const { track, slides, thumbs } = getSliderEls(slider);
  if (!track || slides.length === 0) return;

  const clamped = Math.max(0, Math.min(index, slides.length - 1));
  track.style.transform = `translateX(-${clamped * 100}%)`;
  slider.dataset.currentIndex = clamped;

  thumbs.forEach((thumb) => {
    thumb.classList.toggle('is-active', Number(thumb.dataset.index) === clamped);
  });
}

document.addEventListener('click', (event) => {
  const prevBtn = event.target.closest('[data-quick-view-slider-prev]');
  const nextBtn = event.target.closest('[data-quick-view-slider-next]');
  const thumbBtn = event.target.closest('[data-quick-view-thumb]');

  if (prevBtn) {
    const slider = prevBtn.closest('[data-quick-view-slider]');
    if (slider) goToSlide(slider, Number(slider.dataset.currentIndex || 0) - 1);
    return;
  }

  if (nextBtn) {
    const slider = nextBtn.closest('[data-quick-view-slider]');
    if (slider) goToSlide(slider, Number(slider.dataset.currentIndex || 0) + 1);
    return;
  }

  if (thumbBtn) {
    const container = thumbBtn.closest('[data-quick-view-section]') || document;
    const slider = container.querySelector('[data-quick-view-slider]');
    if (slider) goToSlide(slider, Number(thumbBtn.dataset.index));
    return;
  }
});

// Touch swipe support
let quickViewTouchStartX = 0;
let quickViewTouchSlider = null;

document.addEventListener(
  'touchstart',
  (event) => {
    const slider = event.target.closest('[data-quick-view-slider]');
    if (!slider) return;
    quickViewTouchSlider = slider;
    quickViewTouchStartX = event.touches[0].clientX;
  },
  { passive: true }
);

document.addEventListener(
  'touchend',
  (event) => {
    if (!quickViewTouchSlider) return;
    const touchEndX = event.changedTouches[0].clientX;
    const delta = touchEndX - quickViewTouchStartX;
    const threshold = 40;
    const currentIndex = Number(quickViewTouchSlider.dataset.currentIndex || 0);

    if (delta > threshold) {
      goToSlide(quickViewTouchSlider, currentIndex - 1);
    } else if (delta < -threshold) {
      goToSlide(quickViewTouchSlider, currentIndex + 1);
    }
    quickViewTouchSlider = null;
  },
  { passive: true }
);